import React, { useState, useEffect, useRef } from 'react';
import { TradeDialogData } from '../types/tradingFloor.types';
import { MonitorPosition } from '../../../types/monitor.types';
import { MODEL_PERSONALITIES } from '../constants/officeLayout';
import { EnrichedModelStats, DaySummary } from '../hooks/useModelStats';

interface TradeDialogProps {
  data: TradeDialogData;
  onClose: () => void;
}

function fmtPL(val: number): string {
  return val >= 0 ? `+$${val.toFixed(2)}` : `-$${Math.abs(val).toFixed(2)}`;
}

function buildInTradeText(model: string, pos: MonitorPosition, stats?: EnrichedModelStats | null): string {
  const info = MODEL_PERSONALITIES[model];
  const asset = info?.instrument ?? pos.instrument;
  const dir = pos.direction.toUpperCase();
  const rStr = pos.currentR >= 0 ? `+${pos.currentR.toFixed(1)}R` : `${pos.currentR.toFixed(1)}R`;

  let text = '';
  if (pos.currentR >= 2.0) {
    text = `Hey! I'm ${model}. I'm ${dir} on ${asset} and CRUSHING it at ${rStr}! Tier ${pos.tier} protection locked in. This is what I live for!`;
  } else if (pos.currentR >= 1.0) {
    text = `I'm ${model}. Got a nice ${dir} trade running on ${asset}. Sitting at ${rStr} with Tier ${pos.tier} protection. Smooth sailing!`;
  } else if (pos.currentR > 0.3) {
    text = `${model} here. I'm ${dir} on ${asset}, currently at ${rStr}. Looking solid so far, watching for the next tier trigger.`;
  } else if (pos.currentR > 0) {
    text = `Hey, ${model} here. I've got a ${dir} position on ${asset}. It's green at ${rStr} but still early... keeping my eyes on the screens.`;
  } else if (pos.currentR > -0.3) {
    text = `It's ${model}... I'm ${dir} on ${asset} and it dipped a bit to ${rStr}. Nothing crazy yet, but I'm getting a little nervous here.`;
  } else if (pos.currentR > -0.5) {
    text = `${model} here... sweating a bit. My ${dir} on ${asset} is at ${rStr}. The stop is holding but this is uncomfortable...`;
  } else {
    text = `This is ${model}... I'm ${dir} on ${asset} at ${rStr}. NOT a good day. Holding on for dear life while the boss is watching!`;
  }

  return text;
}

function buildIdleText(model: string, stats?: EnrichedModelStats | null): string {
  const info = MODEL_PERSONALITIES[model];
  if (!info) {
    return `Hi! I'm ${model}. Just sitting here analyzing charts. No trades right now, waiting for the perfect setup.`;
  }

  // Rich text when stats are available
  if (stats && stats.allTime.totalTrades > 0) {
    const s = stats.allTime;
    const t = stats.today;
    const wr = s.winRate.toFixed(0);
    const pf = s.profitFactor.toFixed(1);

    let text = `Hey! I'm ${model}. I specialize in ${info.instrument}. `;
    text += `My track record: ${s.wins}W/${s.losses}L (${wr}% WR), PF ${pf}. `;

    if (t.totalTrades > 0) {
      text += `Today: ${t.totalTrades} trade${t.totalTrades > 1 ? 's' : ''}, ${fmtPL(t.totalPL)}. `;
    } else {
      text += `No trades today yet. `;
    }

    if (stats.lastTrades.length > 0) {
      const last = stats.lastTrades[0];
      text += `Last trade was a ${last.isWin ? 'win' : 'loss'} (${fmtPL(last.profitLoss)}). `;
    }

    text += info.personality;
    return text;
  }

  // Fallback when no stats
  const greetings = [
    `Hey there! I'm ${model}. I specialize in ${info.instrument}. ${info.personality} No active trades right now, just running analysis and waiting for a signal.`,
    `${model} here! ${info.personality} I'm watching ${info.instrument} charts closely. The market's been quiet for ${info.specialty}... but when it moves, I'll be ready.`,
    `Oh hey! I'm ${model}, the ${info.specialty} specialist. ${info.personality} Currently on standby. Sometimes the best trade is no trade at all.`,
  ];

  let hash = 0;
  for (let i = 0; i < model.length; i++) hash = ((hash << 5) - hash) + model.charCodeAt(i);
  return greetings[Math.abs(hash) % greetings.length];
}

function buildManagerText(totalPL: number, totalPositions: number, daySummary?: DaySummary | null): string {
  // Rich day summary when available
  if (daySummary && daySummary.totalTrades > 0) {
    const ds = daySummary;
    let text = `The Boss here. Daily report: ${ds.totalTrades} trade${ds.totalTrades > 1 ? 's' : ''} today`;
    text += ` - ${ds.wins}W/${ds.losses}L. Day P/L: ${fmtPL(ds.totalPL)}. `;

    if (ds.bestModel) {
      text += `Best performer: ${ds.bestModel.name} (${fmtPL(ds.bestModel.pl)}). `;
    }
    if (ds.worstModel && ds.worstModel.name !== ds.bestModel?.name) {
      text += `Worst: ${ds.worstModel.name} (${fmtPL(ds.worstModel.pl)}). `;
    }

    if (totalPositions > 0) {
      text += `Currently managing ${totalPositions} active position${totalPositions > 1 ? 's' : ''}. `;
    }
    text += 'Keep up the discipline!';
    return text;
  }

  // Fallback to existing
  if (totalPositions === 0) {
    return "I'm The Boss. Managing this whole operation. Nobody's in a trade right now. The team is analyzing markets and waiting for setups. Patience pays.";
  }
  if (totalPL > 100) {
    return `The Boss here. We've got ${totalPositions} active positions and the portfolio is up $${totalPL.toFixed(2)}. Great work team! Keep the discipline.`;
  }
  if (totalPL > 0) {
    return `I'm The Boss. ${totalPositions} positions open, portfolio green at $${totalPL.toFixed(2)}. Cautiously optimistic. Let's lock in some profits.`;
  }
  if (totalPL > -50) {
    return `The Boss... portfolio is down $${Math.abs(totalPL).toFixed(2)} with ${totalPositions} open trades. It happens. Risk management is key.`;
  }
  return `THIS IS THE BOSS. Portfolio DOWN $${Math.abs(totalPL).toFixed(2)} with ${totalPositions} open positions. Someone better have a good explanation!`;
}

const TradeDialog: React.FC<TradeDialogProps> = ({ data, onClose }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showStats, setShowStats] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { position, displayName, isManager, totalPL, totalPositions, allPositions } = data;
  const isDCAGroup = (allPositions?.length ?? 0) > 1;
  const isProfit = position ? position.currentPL >= 0 : (totalPL ?? 0) >= 0;

  // Compute fullText ONCE when dialog opens
  const fullTextRef = useRef('');
  const dialogIdRef = useRef('');
  const dialogId = `${data.modelName}-${data.screenX}-${data.screenY}`;

  if (dialogIdRef.current !== dialogId) {
    dialogIdRef.current = dialogId;
    fullTextRef.current = isManager
      ? buildManagerText(totalPL ?? 0, totalPositions ?? 0, data.daySummary)
      : position
        ? buildInTradeText(data.modelName, position, data.modelStats)
        : buildIdleText(data.modelName, data.modelStats);
  }

  const fullText = fullTextRef.current;

  useEffect(() => {
    setDisplayedText('');
    setShowStats(false);
    let i = 0;
    const text = fullTextRef.current;

    intervalRef.current = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(text.slice(0, i + 1));
        i++;
      } else {
        setShowStats(true);
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    }, 25);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogId]);

  const handleSkip = () => {
    if (displayedText.length < fullText.length) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setDisplayedText(fullText);
      setShowStats(true);
    } else {
      onClose();
    }
  };

  const info = MODEL_PERSONALITIES[data.modelName];
  const ms = data.modelStats;

  return (
    <div className="rpg-dialog-overlay" onClick={handleSkip}>
      <div className="rpg-dialog-box" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="rpg-dialog-header">
          <span className="rpg-dialog-name">{displayName}</span>
          {position && (
            <span className={`rpg-dialog-status ${isProfit ? 'profit' : 'loss'}`}>
              {isDCAGroup ? (isProfit ? '[ DCA WINNING ]' : '[ DCA LOSING ]') : (isProfit ? '[ WINNING ]' : '[ LOSING ]')}
            </span>
          )}
          {isManager && (
            <span className={`rpg-dialog-status ${isProfit ? 'profit' : 'loss'}`}>
              {isProfit ? '[ HAPPY ]' : '[ STRESSED ]'}
            </span>
          )}
          {!position && !isManager && (
            <span className="rpg-dialog-status idle">[ IDLE ]</span>
          )}
        </div>

        {/* Typewriter text */}
        <div className="rpg-dialog-content">
          <p className="rpg-dialog-text">
            {displayedText}
            {displayedText.length < fullText.length && (
              <span className="rpg-dialog-cursor">_</span>
            )}
          </p>
        </div>

        {/* Stats panel - active trade */}
        {showStats && position && (
          <div className="rpg-dialog-stats">
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">INSTRUMENT</span>
              <span className="rpg-stat-value">{position.instrument}</span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">DIRECTION</span>
              <span className="rpg-stat-value">{position.direction}</span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">P/L</span>
              <span className={`rpg-stat-value ${isProfit ? 'stat-profit' : 'stat-loss'}`}>
                ${position.currentPL.toFixed(2)}
              </span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">R MULTIPLE</span>
              <span className={`rpg-stat-value ${position.currentR >= 0 ? 'stat-profit' : 'stat-loss'}`}>
                {position.currentR >= 0 ? '+' : ''}{position.currentR.toFixed(2)}R
              </span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">TIER</span>
              <span className="rpg-stat-value">
                {position.tier === 0 ? 'No Tier' : `Tier ${position.tier}`}
              </span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">ENTRY</span>
              <span className="rpg-stat-value">{position.entryPrice}</span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">CURRENT</span>
              <span className="rpg-stat-value">{position.currentPrice}</span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">STOP</span>
              <span className="rpg-stat-value">{position.stopLevel}</span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">DURATION</span>
              <span className="rpg-stat-value">{position.duration}</span>
            </div>
            {isDCAGroup && allPositions && (
              <>
                <div className="rpg-stat-row" style={{ gridColumn: '1 / -1', borderTop: '1px solid #363B56', paddingTop: '4px', marginTop: '4px' }}>
                  <span className="rpg-stat-label">DCA GROUP</span>
                  <span className="rpg-stat-value">{allPositions.length} entries</span>
                </div>
                {allPositions.map((entry, i) => (
                  <div className="rpg-stat-row" key={entry.tradeId}>
                    <span className="rpg-stat-label">ENTRY {i + 1}</span>
                    <span className={`rpg-stat-value ${entry.currentPL >= 0 ? 'stat-profit' : 'stat-loss'}`}>
                      {fmtPL(entry.currentPL)} ({entry.currentR >= 0 ? '+' : ''}{entry.currentR.toFixed(1)}R)
                    </span>
                  </div>
                ))}
              </>
            )}
            {!isDCAGroup && position.dcaEntriesFilled != null && (
              <div className="rpg-stat-row">
                <span className="rpg-stat-label">DCA</span>
                <span className="rpg-stat-value">
                  {position.dcaEntriesFilled}/{position.dcaMaxEntries} ({position.dcaStatus})
                </span>
              </div>
            )}
            {ms?.allTime && ms.allTime.totalTrades > 0 && (
              <div className="rpg-stat-row" style={{ gridColumn: '1 / -1', borderTop: '1px solid #363B56', paddingTop: '4px', marginTop: '4px' }}>
                <span className="rpg-stat-label">RECORD</span>
                <span className="rpg-stat-value">
                  {ms.allTime.wins}W/{ms.allTime.losses}L ({ms.allTime.winRate.toFixed(0)}%) PF {ms.allTime.profitFactor.toFixed(1)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Stats panel - idle agent */}
        {showStats && !position && !isManager && (
          <div className="rpg-dialog-stats">
            {info && (
              <>
                <div className="rpg-stat-row">
                  <span className="rpg-stat-label">ASSET</span>
                  <span className="rpg-stat-value">{info.instrument}</span>
                </div>
                <div className="rpg-stat-row">
                  <span className="rpg-stat-label">SPECIALTY</span>
                  <span className="rpg-stat-value">{info.specialty}</span>
                </div>
              </>
            )}
            {ms?.allTime && ms.allTime.totalTrades > 0 ? (
              <>
                <div className="rpg-stat-row">
                  <span className="rpg-stat-label">RECORD</span>
                  <span className="rpg-stat-value">{ms.allTime.wins}W/{ms.allTime.losses}L</span>
                </div>
                <div className="rpg-stat-row">
                  <span className="rpg-stat-label">WIN RATE</span>
                  <span className="rpg-stat-value">{ms.allTime.winRate.toFixed(0)}%</span>
                </div>
                <div className="rpg-stat-row">
                  <span className="rpg-stat-label">PROFIT FACTOR</span>
                  <span className="rpg-stat-value">{ms.allTime.profitFactor.toFixed(2)}</span>
                </div>
                <div className="rpg-stat-row">
                  <span className="rpg-stat-label">TOTAL P/L</span>
                  <span className={`rpg-stat-value ${ms.allTime.totalPL >= 0 ? 'stat-profit' : 'stat-loss'}`}>
                    ${ms.allTime.totalPL.toFixed(2)}
                  </span>
                </div>
                {ms.today.totalTrades > 0 && (
                  <div className="rpg-stat-row">
                    <span className="rpg-stat-label">TODAY</span>
                    <span className={`rpg-stat-value ${ms.today.totalPL >= 0 ? 'stat-profit' : 'stat-loss'}`}>
                      {ms.today.totalTrades} trade{ms.today.totalTrades > 1 ? 's' : ''} ({fmtPL(ms.today.totalPL)})
                    </span>
                  </div>
                )}
                <div className="rpg-stat-row">
                  <span className="rpg-stat-label">AVG WIN</span>
                  <span className="rpg-stat-value stat-profit">${ms.allTime.avgPerWin.toFixed(2)}</span>
                </div>
                <div className="rpg-stat-row">
                  <span className="rpg-stat-label">AVG LOSS</span>
                  <span className="rpg-stat-value stat-loss">${Math.abs(ms.allTime.avgPerLoss).toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div className="rpg-stat-row">
                <span className="rpg-stat-label">STATUS</span>
                <span className="rpg-stat-value" style={{ color: '#94A3B8' }}>Waiting for signal</span>
              </div>
            )}
          </div>
        )}

        {/* Stats panel - manager */}
        {showStats && isManager && (
          <div className="rpg-dialog-stats">
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">LIVE P/L</span>
              <span className={`rpg-stat-value ${isProfit ? 'stat-profit' : 'stat-loss'}`}>
                ${(totalPL ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">POSITIONS</span>
              <span className="rpg-stat-value">{totalPositions ?? 0}</span>
            </div>
            <div className="rpg-stat-row">
              <span className="rpg-stat-label">TEAM SIZE</span>
              <span className="rpg-stat-value">12 agents</span>
            </div>
            {data.daySummary && data.daySummary.totalTrades > 0 && (
              <>
                <div className="rpg-stat-row" style={{ gridColumn: '1 / -1', borderTop: '1px solid #363B56', paddingTop: '4px', marginTop: '4px' }}>
                  <span className="rpg-stat-label">TODAY</span>
                  <span className="rpg-stat-value">
                    {data.daySummary.totalTrades} trades ({data.daySummary.wins}W/{data.daySummary.losses}L)
                  </span>
                </div>
                <div className="rpg-stat-row">
                  <span className="rpg-stat-label">DAY P/L</span>
                  <span className={`rpg-stat-value ${data.daySummary.totalPL >= 0 ? 'stat-profit' : 'stat-loss'}`}>
                    {fmtPL(data.daySummary.totalPL)}
                  </span>
                </div>
                {data.daySummary.bestModel && (
                  <div className="rpg-stat-row">
                    <span className="rpg-stat-label">BEST</span>
                    <span className="rpg-stat-value stat-profit">
                      {data.daySummary.bestModel.name} ({fmtPL(data.daySummary.bestModel.pl)})
                    </span>
                  </div>
                )}
                {data.daySummary.worstModel && data.daySummary.worstModel.name !== data.daySummary.bestModel?.name && (
                  <div className="rpg-stat-row">
                    <span className="rpg-stat-label">WORST</span>
                    <span className="rpg-stat-value stat-loss">
                      {data.daySummary.worstModel.name} ({fmtPL(data.daySummary.worstModel.pl)})
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="rpg-dialog-footer">
          <span className="rpg-dialog-hint" onClick={onClose}>
            {displayedText.length < fullText.length
              ? '[ Click to skip ]'
              : '[ Click to close ]'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TradeDialog;
